import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { useEditing } from '@src/state/editing/hooks';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useTIN } from '@src/state/taxInformation';
import { useUser } from '@src/state/user';
import { useOnloading } from '@src/state/landing';
import { useFormState } from '@src/state/form/hooks';
import { setPortalAccess } from '@src/state/form/actions';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { adobeInteraction, getOrgType } from '../helpers';

const ContinueButton = styled('div', {
  width: '126px',
  height: '42px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '21px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const ContinueButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const CancelButton = styled('div', {
  width: '89px',
  height: '42px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: ' 2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '21px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const CancelButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  fontSize: '16px',
});

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
  paddingBottom: '20px',
});

const BoxHeader = styled('div', {
  position: 'relative',
  marginBottom: '5px',
});

const BoxHeaderText = styled('h3', {
  fontWeight: 600,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
});

const Description = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  marginBottom: '20px',
  fontFamily: '$primary',
});

const BackButton = styled('div', {
  width: '115px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const NextButton = styled('div', {
  float: 'right',
  width: '115px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const BackButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '12px',
  fontSize: '16px',
});

const NextButtonText = styled('text', {
  color: '$white',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
});

const ThinHorizontalLine = styled('div', {
  boxSizing: 'border-box',
  height: '1px',
  width: '600px',
  border: '1px solid #DADBDC',
  marginBottom: '10px',
});

const LeftIndentation = styled('div', {
  paddingLeft: '20px',
});

const Space = styled('div', {});

const SubPermissionDescription = styled('div', {
  fontSize: '14px',
  marginBottom: '20px',
  paddingLeft: '25px',
  lineHeight: 1.25,
  marginTop: '8px',
  fontFamily: '$primary',
});

const CheckboxSubpermission = styled('div', {
  margin: '30px 10px 16px 0px !important',
  height: '68px',
});

const SelectAllCheck = styled('div', {
  height: '30px',
  width: '627px',
  marginBottom: '5px',
});

const MarginCheckbox = styled('div', {
  marginBottom: '10px',
});

const SubPermissionItem = ({
  id,
  name,
  description,
  value,
  onChecked,
  isAmaSelected,
}) => {
  const [checked, setChecked] = useState(value);

  useEffect(() => {
    setChecked(value);
  }, [value]);

  const handleSubPermissionChecked = (e) => {
    if (!isAmaSelected) {
      setChecked(e.target.checked);
      onChecked(name, e.target.checked);
    }
  };

  return (
    <React.Fragment>
      <div>
        <Checkbox
          size="sm"
          onChange={handleSubPermissionChecked}
          aria-label=""
          isChecked={checked}
          defaultValue={checked}
          isDisabled={isAmaSelected}
          label={name}
        />
        <SubPermissionDescription>{description}</SubPermissionDescription>
      </div>
      <Space />
    </React.Fragment>
  );
};

const PermissionItem = ({
  id,
  value,
  permission,
  subPermissions,
  selectAllPermissions,
  onPermissionChecked,
  onSelectAllSubPermissions,
  onSubPermissionChecked,
  isAmaSelected,
  code,
}) => {
  const [showSubPermissions, setShowSubPermissions] = useState(value);
  const [subPermissionCheckboxes, setSubPermissionCheckboxes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const { actions, state } = useFormState({ setPortalAccess });

  useEffect(() => {
    setSelectAll(
      state.registration?.portalAccess?.selectAllSubPermissions || ''
    );
  }, []);

  useEffect(() => {
    setSelectAll(
      state.registration?.portalAccess?.selectAllSubPermissions || ''
    );
  }, [selectAllPermissions]);

  useEffect(() => {
    setSelectAll(
      state.registration?.portalAccess?.selectAllSubPermissions || ''
    );
  }, [selectAll]);

  const validateSubPermissions = () => {
    if (subPermissions && subPermissions.length > 0) {
      const anySelected = subPermissions.filter((perm) => {
        return perm.value === true;
      });
      if (!(anySelected && anySelected.length > 0)) {
        actions.setPortalAccess({ selectAllSubPermissions: false });
      }
    }
  };

  useEffect(() => {
    validateSubPermissions();
    return () => {
      validateSubPermissions();
    };
  }, []);

  useEffect(() => {
    if (
      subPermissions &&
      Array.isArray(subPermissions) &&
      subPermissions.length > 0
    ) {
      const allSelected = subPermissions?.filter((p) => {
        return p.value === true;
      });
      if (allSelected.length === subPermissions.length) {
        setSelectAll(true);
        actions.setPortalAccess({ selectAllSubPermissions: true });
      }
      setSubPermissionCheckboxes([...subPermissions]);
    }
    validateSubPermissions();
  }, [subPermissions]);

  useEffect(() => {
    setShowSubPermissions(value);
  }, [value]);

  const handlePermissionChecked = (e) => {
    if (!isAmaSelected) {
      setShowSubPermissions(e.target.checked);
      if (!e.target.checked) {
        setSelectAll(e.target.checked);
      }
    }
    onPermissionChecked(id, e.target.checked);
  };

  const handleSelectAllChecked = (e) => {
    if (!isAmaSelected) {
      setSelectAll(e.target.checked);
      const newSubPermissionCheckboxes = subPermissionCheckboxes.map(
        (subPermissionCheckbox) => {
          const obj = { ...subPermissionCheckbox };
          obj.value = e.target.checked;
          return obj;
        }
      );
      setSubPermissionCheckboxes([...newSubPermissionCheckboxes]);
      onSelectAllSubPermissions(id, e.target.checked);
    }
  };

  const handleSubPermissionChecked = (name, checked) => {
    if (!isAmaSelected) {
      if (!checked) {
        setSelectAll(checked);
      }
      onSubPermissionChecked(id, name, checked);
    }
  };

  return (
    <React.Fragment>
      <SelectAllCheck>
        <Checkbox
          data-testid={permission}
          size="sm"
          onChange={handlePermissionChecked}
          aria-label=""
          isChecked={value}
          isDisabled={code !== 'AMA' && isAmaSelected}
          defaultValue={value}
          label={permission}
        />
      </SelectAllCheck>
      {showSubPermissions &&
      subPermissionCheckboxes &&
      Array.isArray(subPermissionCheckboxes) &&
      subPermissionCheckboxes.length > 0 ? (
        <LeftIndentation>
          <Checkbox
            size="sm"
            onChange={handleSelectAllChecked}
            aria-label=""
            isChecked={selectAll}
            defaultValue={selectAll}
            isDisabled={isAmaSelected}
            label="Select All"
            css={{
              'abyss-checkbox-root': {
                marginBottom: '16px',
              },
            }}
          />
          <ThinHorizontalLine />
          {subPermissionCheckboxes.map((subPermissionCheckbox) => {
            return (
              <CheckboxSubpermission>
                <SubPermissionItem
                  key={`${id}_${subPermissionCheckbox.name}`}
                  id={id}
                  isAmaSelected={isAmaSelected}
                  isDisabled={isAmaSelected}
                  {...subPermissionCheckbox}
                  onChecked={handleSubPermissionChecked}
                />
              </CheckboxSubpermission>
            );
          })}
        </LeftIndentation>
      ) : null}
    </React.Fragment>
  );
};

export const PortalAccess = ({
  permissions,
  selectedPermissions,
  preserveSelectedPermissions,
  handleNext,
  handleBack,
}) => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const { isEditing } = useEditing.state();
  const [selectAllPermissions, setSelectAllPermissions] = useState(false);
  const [permissionCheckboxes, setPermissionCheckboxes] = useState([]);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [errorBorder, setErrorBorder] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { TINNumber, TINInfo } = useTIN.state();
  const { userUUID } = useUser.state();
  const { currentFlow } = useOnloading.state();
  const { actions } = useFormState({ setPortalAccess });
  const [isAmaSelected, setIsAmaSelected] = useState(false);

  const APPROVE_AND_MANAGE_ACCESS = 'Approve and Manage Access';
  const ERROR_MESSAGE_ATLEAST_ONE_PERMISSION =
    'Error: Select at least one permission in order to proceed';
  const ERROR_MESSAGE_ATLEAST_ONE_SUBPERMISSION =
    'Error: Select at least one subpermission of selected permission(s) in order to proceed';

  useEffect(() => {
    const allSelected =
      permissionCheckboxes.length > 0 &&
      permissionCheckboxes.filter((p) => {
        return p.value === true;
      });
    if (allSelected.length === 6) {
      setSelectAllPermissions(true);
    }
    const subPerm = permissionCheckboxes[permissionCheckboxes.length - 1];
    const perms = subPerm?.subPermissions;

    const allSubPermSelected =
      perms?.filter((p) => {
        return p.value === true;
      }).length === perms?.length;
    if (allSubPermSelected) {
      actions.setPortalAccess({ selectAllSubPermissions: true });
    } else {
      actions.setPortalAccess({ selectAllSubPermissions: false });
    }
  }, [permissionCheckboxes]);

  useEffect(() => {
    if (!permissions || permissions.length <= 0) return;

    permissions.sort((p1, p2) => {
      return p1.order - p2.order;
    });

    if (!selectedPermissions) return;

    const permissionsForCheckboxes = permissions.map((permission) => {
      const obj = { ...permission };
      const { subPermissions } = permission;
      const matchingSelectedPermission = selectedPermissions.filter(
        (selectedPermission) => {
          return selectedPermission.id === permission.id;
        }
      )[0];
      if (!matchingSelectedPermission) {
        obj.value = false;
        if (
          subPermissions &&
          Array.isArray(subPermissions) &&
          subPermissions.length > 0
        ) {
          const newSubPermissions = subPermissions.map((subPermission) => {
            const subObj = { ...subPermission };
            subObj.value = false;
            return subObj;
          });
          obj.subPermissions = newSubPermissions;
        }
      } else if (
        subPermissions &&
        Array.isArray(subPermissions) &&
        subPermissions.length > 0
      ) {
        const { subPermissions: selectedSubPermissions } = {
          ...matchingSelectedPermission,
        };
        if (
          selectedSubPermissions &&
          Array.isArray(selectedSubPermissions) &&
          selectedSubPermissions.length > 0
        ) {
          const newSubPermissions = subPermissions.map((subPermission) => {
            const subObj = { ...subPermission };
            subObj.value =
              selectedSubPermissions.filter(({ name }) => {
                return name === subPermission.name;
              }).length > 0;
            return subObj;
          });
          obj.value =
            newSubPermissions.filter(({ value }) => {
              return value;
            }).length > 0;
          obj.subPermissions = newSubPermissions;
        } else {
          obj.value = false;
          const newSubPermissions = subPermissions.map((subPermission) => {
            const subObj = { ...subPermission };
            subObj.value = false;
            return subObj;
          });
          obj.subPermissions = newSubPermissions;
        }
      } else {
        obj.value = true;
      }
      return obj;
    });
    const allSelected =
      permissionsForCheckboxes.length > 0 &&
      permissionsForCheckboxes.filter(({ value }) => {
        return value;
      }).length === permissionsForCheckboxes.length;
    if (allSelected) {
      setSelectAllPermissions(true);
    }
    setPermissionCheckboxes([...permissionsForCheckboxes]);
    const amaPermission = permissionsForCheckboxes.filter((e) => {
      return e.code === 'AMA';
    })[0];
    if (amaPermission) {
      setIsAmaSelected(amaPermission.value);
    }
  }, [permissions]);

  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'tin registration';
      const section3 = 'portal access task selection';
      const pageName = `digitalsecurity:${section1}:${orgType}:${section3}`;
      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2: orgType,
        section3,
      });
    }
  }, []);

  const isValidateSelection = () => {
    const selectedPermissionCheckboxes = permissionCheckboxes.filter(
      ({ value }) => {
        return value;
      }
    );

    if (selectedPermissionCheckboxes.length <= 0) {
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const fields = ['Portal Access - no permissions selected'];
        adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
      }
      setErrorMessage(ERROR_MESSAGE_ATLEAST_ONE_PERMISSION);
      return false;
    }

    const countOfPermissionsWithNoSubPermissionsSelected =
      selectedPermissionCheckboxes
        .filter(({ subPermissions }) => {
          return (
            subPermissions &&
            Array.isArray(subPermissions) &&
            subPermissions.length > 0
          );
        })
        .map(({ subPermissions }) => {
          return subPermissions.filter(({ value }) => {
            return value;
          });
        })
        .filter(({ length }) => {
          return length <= 0;
        }).length;

    if (countOfPermissionsWithNoSubPermissionsSelected > 0) {
      if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
        const fields = [
          'Portal Access - at least one reporting subpermission must be selected',
        ];
        adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
      }
      setErrorMessage(ERROR_MESSAGE_ATLEAST_ONE_SUBPERMISSION);
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const extractSelectedPermissions = () => {
    const newSelectedPermissions = permissionCheckboxes
      .filter(({ value }) => {
        return value;
      })
      .map((selectedPermissionCheckbox) => {
        const { id, permission, subPermissions, order, isPaa, value, code } = {
          ...selectedPermissionCheckbox,
        };
        const obj = {};
        obj.id = id;
        obj.permission = permission;
        obj.order = order;
        obj.isPaa = isPaa;
        obj.code = code;
        if (
          subPermissions &&
          Array.isArray(subPermissions) &&
          subPermissions.length > 0
        ) {
          if (value) {
            const selectedSubPermissions = subPermissions
              .filter((subPermission) => {
                return subPermission.value;
              })
              .map((subPermission) => {
                const { name, description, subCode } = {
                  ...subPermission,
                };
                const subObj = {};
                subObj.name = name;
                subObj.description = description;
                subObj.code = subPermission.code;
                subObj.subCode = subCode;
                return subObj;
              });
            obj.subPermissions = selectedSubPermissions;
          }
        }
        return obj;
      });
    return newSelectedPermissions;
  };

  const onClickModalConfirm = () => {
    setOpenApproveDialog(false);
    const newSelectedPermissions = extractSelectedPermissions();
    preserveSelectedPermissions(newSelectedPermissions);
    adobeInteraction(
      'Portal Access',
      'confirm',
      currentFlow,
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
    );
    handleNext();
  };

  const onClickNextButton = () => {
    const valid = isValidateSelection();
    if (!valid) {
      setErrorBorder(true);
      return;
    }
    const approveCheckboxSelected =
      permissionCheckboxes.filter(({ permission, value }) => {
        return value && permission === APPROVE_AND_MANAGE_ACCESS;
      }).length > 0;

    if (approveCheckboxSelected) {
      setOpenApproveDialog(approveCheckboxSelected);
      return;
    }

    const newSelectedPermissions = extractSelectedPermissions();
    preserveSelectedPermissions(newSelectedPermissions);
    handleNext();
  };

  const onSelectAllChecked = (e) => {
    setSelectAllPermissions(e.target.checked);
    const newPermissionCheckboxes = permissionCheckboxes.map(
      (permissionCheckbox) => {
        const obj = { ...permissionCheckbox };
        obj.value = e.target.checked;
        const { subPermissions } = obj;
        if (
          subPermissions &&
          Array.isArray(subPermissions) &&
          subPermissions.length > 0
        ) {
          if (!e.target.checked) {
            setIsAmaSelected(false);
            actions.setPortalAccess({ selectAllSubPermissions: false });
            const newSubPermissions = subPermissions.map((subPermission) => {
              const subObj = { ...subPermission };
              subObj.value = e.target.checked;
              return subObj;
            });
            obj.subPermissions = newSubPermissions;
          } else {
            setIsAmaSelected(true);
            const newSubPermissions = subPermissions.map((subPermission) => {
              const subObj = { ...subPermission };
              subObj.value = true;
              return subObj;
            });
            obj.subPermissions = newSubPermissions;
          }
        }
        return obj;
      }
    );
    setPermissionCheckboxes([...newPermissionCheckboxes]);
    setErrorBorder(false);
    setErrorMessage('');
  };

  const handleAmaSelect = () => {
    setSelectAllPermissions(true);
    setIsAmaSelected(true);

    const newPermissionCheckboxes = permissionCheckboxes.map(
      (permissionCheckbox) => {
        const obj = { ...permissionCheckbox };
        obj.value = true;

        const { subPermissions } = obj;
        if (
          subPermissions &&
          Array.isArray(subPermissions) &&
          subPermissions.length > 0
        ) {
          const newSubPermissions = subPermissions.map((subPermission) => {
            const subObj = { ...subPermission };
            subObj.value = true;
            return subObj;
          });
          obj.subPermissions = newSubPermissions;
        }

        return obj;
      }
    );
    setPermissionCheckboxes([...newPermissionCheckboxes]);
    setErrorBorder(false);
    setErrorMessage('');
  };
  const handleAmaVerify = (id, checked) => {
    const amaSelected = permissionCheckboxes.filter((e) => {
      return e.code === 'AMA';
    })[0];
    if (
      !checked &&
      amaSelected &&
      amaSelected.id !== id &&
      amaSelected.value === true
    ) {
      return true;
    }
    return false;
  };
  const onPermissionChecked = (id, checked) => {
    const amaPermission = permissionCheckboxes.filter((e) => {
      return e.code === 'AMA';
    })[0];

    if (
      checked &&
      amaPermission &&
      amaPermission.id === id &&
      amaPermission.code === 'AMA'
    ) {
      handleAmaSelect();
      return;
    }

    if (handleAmaVerify(id, checked)) return;
    if (!checked) {
      setSelectAllPermissions(checked);
    }
    const newPermissionCheckboxes = permissionCheckboxes.map(
      (permissionCheckbox) => {
        const obj = { ...permissionCheckbox };
        if (obj.id === id) {
          obj.value = checked;
          if (!checked) {
            const { subPermissions } = obj;
            if (
              subPermissions &&
              Array.isArray(subPermissions) &&
              subPermissions.length > 0
            ) {
              const newSubPermissions = subPermissions.map((subPermission) => {
                const subObj = { ...subPermission };
                subObj.value = checked;
                return subObj;
              });
              obj.subPermissions = newSubPermissions;
            }
          }
        }
        return obj;
      }
    );
    setPermissionCheckboxes([...newPermissionCheckboxes]);
    setErrorBorder(false);
    setErrorMessage('');
    setIsAmaSelected(false);
  };

  const onSelectAllSubPermissions = (id, checked) => {
    if (handleAmaVerify(id, checked)) return;
    const newPermissionCheckboxes = permissionCheckboxes.map(
      (permissionCheckbox) => {
        const obj = { ...permissionCheckbox };
        if (obj.id === id) {
          const { subPermissions } = obj;
          if (
            subPermissions &&
            Array.isArray(subPermissions) &&
            subPermissions.length > 0
          ) {
            const newSubPermissions = subPermissions.map((subPermission) => {
              const subObj = { ...subPermission };
              subObj.value = checked;
              return subObj;
            });
            obj.subPermissions = newSubPermissions;
          }
        }
        return obj;
      }
    );
    setPermissionCheckboxes([...newPermissionCheckboxes]);
    setErrorBorder(false);
    setErrorMessage('');
  };

  const onSubPermissionChecked = (id, name, checked) => {
    if (handleAmaVerify(id, checked)) return;
    const newPermissionCheckboxes = permissionCheckboxes.map(
      (permissionCheckbox) => {
        const obj = { ...permissionCheckbox };
        if (obj.id === id) {
          const { subPermissions } = obj;
          if (
            subPermissions &&
            Array.isArray(subPermissions) &&
            subPermissions.length > 0
          ) {
            const newSubPermissions = subPermissions.map((subPermission) => {
              const subObj = { ...subPermission };
              if (subObj.name === name) {
                subObj.value = checked;
              }
              return subObj;
            });
            obj.subPermissions = newSubPermissions;
          }
        }
        return obj;
      }
    );
    setPermissionCheckboxes([...newPermissionCheckboxes]);
    setErrorBorder(false);
    setErrorMessage('');
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={openApproveDialog}
        onClose={() => {
          return setOpenApproveDialog(false);
        }}
        title="Approve and Manage Access Permissions"
        size="md"
      >
        <Modal.Section>
          <p>
            Approve and Manage Access permissions on this UnitedHealthcare
            Provider Portal Account need to be approved by your organization’s
            administrator.
          </p>
        </Modal.Section>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: '10px 20px',
          }}
        >
          <CancelButton
            onClick={() => {
              setOpenApproveDialog(false);
              adobeInteraction(
                'Portal Access',
                'cancel',
                currentFlow,
                featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
              );
            }}
          >
            <CancelButtonText>Cancel</CancelButtonText>
          </CancelButton>
          <ContinueButton
            onClick={() => {
              return onClickModalConfirm();
            }}
          >
            <ContinueButtonText>Confirm</ContinueButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </ContinueButton>
        </div>
      </Modal>
      <MainContainer>
        <BoxHeader>
          <BoxHeaderText>
            Select the tasks that you need access to.
          </BoxHeaderText>
        </BoxHeader>
        <Description>Please select all that apply.</Description>
        <div
          style={{
            marginBottom: '5px',
            marginTop: '5px',
            padding: '10px',
            border: errorBorder ? '2px solid rgb(208 2 28)' : 'none',
            borderRadius: '4px',
          }}
        >
          <MarginCheckbox>
            <Checkbox
              data-testid="SelectAllTask"
              size="sm"
              onChange={onSelectAllChecked}
              aria-label=""
              isChecked={selectAllPermissions}
              defaultValue={selectAllPermissions}
              label="Select All"
            />
          </MarginCheckbox>
          <ThinHorizontalLine />
          {permissionCheckboxes.map((permissionCheckbox) => {
            return (
              <PermissionItem
                key={permissionCheckbox.id}
                {...permissionCheckbox}
                selectAllPermissions={selectAllPermissions}
                onPermissionChecked={onPermissionChecked}
                onSelectAllSubPermissions={onSelectAllSubPermissions}
                onSubPermissionChecked={onSubPermissionChecked}
                isAmaSelected={isAmaSelected}
              />
            );
          })}
        </div>
        {errorBorder ? (
          <p
            style={{
              color: 'rgb(208 2 28)',
              fontSize: '80%',
              fontWeight: '600',
            }}
          >
            {errorMessage}
          </p>
        ) : null}
      </MainContainer>
      <Flex
        css={{
          'abyss-flex-root': {
            justifyContent: 'space-between',
          },
        }}
      >
        <Flex.Content space-50>
          {!isEditing && (
            <BackButton onClick={handleBack}>
              <IconSymbol icon="arrow_back" size={20} color="$primary1" />
              <BackButtonText>Back</BackButtonText>
            </BackButton>
          )}
        </Flex.Content>
        <Flex.Content space-50>
          <NextButton
            onClick={() => {
              return onClickNextButton();
            }}
          >
            <NextButtonText>{isEditing ? 'Save' : 'Next'}</NextButtonText>
            <IconSymbol icon="arrow_forward" size={16} color="$primary2" />
          </NextButton>
        </Flex.Content>
      </Flex>
    </React.Fragment>
  );
};

PortalAccess.propTypes = {
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      permission: PropTypes.string.isRequired,
      subPermissions: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        })
      ),
      order: PropTypes.number.isRequired,
      isPaa: PropTypes.bool.isRequired,
    })
  ).isRequired,
  selectedPermissions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      permission: PropTypes.string.isRequired,
      subPermissions: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
        })
      ),
      order: PropTypes.number.isRequired,
      isPaa: PropTypes.bool.isRequired,
    })
  ),
  preserveSelectedPermissions: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
};

PortalAccess.defaultProps = {
  selectedPermissions: [],
};

PermissionItem.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.bool.isRequired,
  permission: PropTypes.string.isRequired,
  subPermissions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ),
  selectAllPermissions: PropTypes.bool.isRequired,
  onPermissionChecked: PropTypes.func.isRequired,
  onSelectAllSubPermissions: PropTypes.func.isRequired,
  onSubPermissionChecked: PropTypes.func.isRequired,
  isAmaSelected: PropTypes.bool.isRequired,
  code: PropTypes.string,
};

PermissionItem.defaultProps = {
  subPermissions: undefined,
  code: '',
};

SubPermissionItem.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  value: PropTypes.bool.isRequired,
  onChecked: PropTypes.func.isRequired,
  isAmaSelected: PropTypes.bool.isRequired,
};
